import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import AuthContext from '../../../contexts/AuthContext';
import UIContext from '../../../contexts/UIContext';
import { IDashboardProjectionPoint } from '../../../Interfaces/IDashboardProjectionPoint';
import { dashboardService } from '../../../services/dashboard';

import './styles.css';

interface IProps {
  userId: number;
  bankId?: number;
}

const positiveColor = '#349199';
const negativeColor = '#ff4d4f';
const neutralLineColor = '#6f6f6f';

const Projection10DaysChart: React.FC<IProps> = ({ userId, bankId }) => {
  const { token } = useContext(AuthContext);
  const { setComponentUIState } = useContext(UIContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<IDashboardProjectionPoint[]>([]);
  const [initialBalance, setInitialBalance] = useState<number>(0);

  const fetchProjection = useCallback(async () => {
    if (!userId || !token) {
      setData([]);
      setInitialBalance(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: response } = await dashboardService.projection10Days(userId, token, bankId);
      setData(response.data || []);
      setInitialBalance(response.initialBalance || 0);
    } catch (error) {
      setComponentUIState({
        component: 'Toast',
        componentState: { open: true, message: 'Falha ao carregar projeção de 10 dias.', type: 'error' }
      });
      setData([]);
      setInitialBalance(0);
    } finally {
      setLoading(false);
    }
  }, [bankId, setComponentUIState, token, userId]);

  useEffect(() => {
    fetchProjection();
  }, [fetchProjection]);

  const chartData = useMemo(() => {
    if (!data.length) return null;

    const currencyValues: number[] = [];

    const barSegments = data.map((point, index) => {
      const entradasSegment = (index === 0 ? initialBalance : 0) + (point.entradas || 0);
      const saidasSegment = point.saidas || 0;

      currencyValues.push(Math.abs(entradasSegment));
      currencyValues.push(Math.abs(saidasSegment));
      currencyValues.push(Math.abs(point.saldo));

      return {
        date: point.date,
        entradasSegment,
        saidasSegment,
        saldo: point.saldo
      };
    });

    const sortedValues = [...currencyValues].sort((a, b) => a - b);
    const percentileIndex = sortedValues.length > 0 ? Math.floor((sortedValues.length - 1) * 0.95) : 0;
    const softMax = sortedValues.length > 0 ? sortedValues[percentileIndex] : 0;

    const domainCap = Math.max(softMax, Math.abs(initialBalance), 1);
    const maxValue = domainCap;
    const minValue = -domainCap;
    const range = maxValue - minValue || 1;

    const topPadding = 10;
    const bottomPadding = 10;
    const chartHeight = 100 - topPadding - bottomPadding;

    const valueToY = (value: number) => {
      return topPadding + (maxValue - value) / range * chartHeight;
    };

    const baseline = valueToY(0);

    const bars = barSegments.map((segment, index) => {
      const isSingle = data.length === 1;
      const step = 100 / (isSingle ? 1 : data.length);
      const x = isSingle ? 50 : (step / 2) + (index * step);
      const width = isSingle ? 8 : Math.max(4, step * 0.55);

      const entradasValue = Math.min(segment.entradasSegment, maxValue);
      const entradasTopY = valueToY(entradasValue);
      const entradasHeight = Math.max(0, baseline - entradasTopY);
      const entradasClipped = segment.entradasSegment > maxValue;

      const saidasValue = Math.min(segment.saidasSegment, maxValue);
      const saidasBottomY = valueToY(-saidasValue);
      const saidasHeight = Math.max(0, saidasBottomY - baseline);
      const saidasClipped = segment.saidasSegment > maxValue;

      return {
        date: segment.date,
        saldo: segment.saldo,
        entradas: {
          x: x - width / 2,
          y: entradasTopY,
          width,
          height: entradasHeight,
          clipped: entradasClipped
        },
        saidas: {
          x: x - width / 2,
          y: baseline,
          width,
          height: saidasHeight,
          clipped: saidasClipped
        },
        point: {
          x,
          y: valueToY(Math.max(Math.min(segment.saldo, maxValue), minValue))
        }
      };
    });

    const lineSegments = bars.map((bar, index, array) => {
      if (index === 0) return null;
      const prev = array[index - 1];
      if (!prev) return null;
      return {
        d: `M ${prev.point.x} ${prev.point.y} L ${bar.point.x} ${bar.point.y}`,
        color: neutralLineColor
      };
    }).filter(Boolean) as { d: string; color: string }[];

    return {
      bars,
      baseline,
      lineSegments,
      maxValue,
      minValue
    };
  }, [data, initialBalance]);

  const dateFormatter = (value: string) => {
    if (!value) return '';
    const [year, month, day] = value.split('-');
    if (!year || !month || !day) return value;
    return `${day}/${month}`;
  };

  const labelStep = useMemo(() => {
    if (!data.length) return 1;
    return Math.max(1, Math.floor(data.length / 6));
  }, [data.length]);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }), []);

  return (
    <div className="projection-chart-container">
      <div className="projection-chart-header">
        <h3>Saldo Acumulado</h3>
        <span className="projection-chart-subtitle">
          Projeção dos próximos 10 dias considerando entradas, saídas e saldo acumulado.
        </span>
      </div>

      {loading ? (
        <Skeleton height={240} borderRadius={12} />
      ) : !chartData ? (
        <div className="projection-chart-empty">
          <span>Sem movimentações futuras. O saldo permanece inalterado.</span>
        </div>
      ) : (
        <>
          <div className="projection-chart-wrapper">
            <svg className="projection-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
              {chartData.bars.map((bar, index) => (
                <g key={`bar-${bar.date}-${index}`}>
                  {bar.saidas.height > 0 && (
                    <rect
                      x={bar.saidas.x}
                      y={bar.saidas.y}
                      width={bar.saidas.width}
                      height={bar.saidas.height}
                      fill={negativeColor}
                      opacity={0.85}
                    />
                  )}
                  {bar.entradas.height > 0 && (
                    <rect
                      x={bar.entradas.x}
                      y={bar.entradas.y}
                      width={bar.entradas.width}
                      height={bar.entradas.height}
                      fill={positiveColor}
                      opacity={0.9}
                    />
                  )}
                  {bar.entradas.height > 0 && bar.entradas.clipped && (
                    <rect
                      x={bar.entradas.x}
                      y={bar.entradas.y}
                      width={bar.entradas.width}
                      height={1.2}
                      fill="#ffffff"
                    />
                  )}
                  {bar.saidas.height > 0 && bar.saidas.clipped && (
                    <rect
                      x={bar.saidas.x}
                      y={bar.saidas.y + bar.saidas.height - 1.2}
                      width={bar.saidas.width}
                      height={1.2}
                      fill="#ffffff"
                    />
                  )}
                </g>
              ))}

              {chartData.lineSegments.map((segment, index) => (
                <path
                  key={`line-segment-${index}`}
                  d={segment.d}
                  fill="none"
                  stroke={segment.color}
                  strokeWidth={1.2}
                />
              ))}

              {chartData.bars.map((bar, index) => (
                <circle
                  key={`point-${bar.date}-${index}`}
                  cx={bar.point.x}
                  cy={bar.point.y}
                  r={1.2}
                  fill={neutralLineColor}
                  stroke="#ffffff"
                  strokeWidth={0.5}
                />
              ))}

              <line
                x1={0}
                y1={chartData.baseline}
                x2={100}
                y2={chartData.baseline}
                stroke="#d9d9d9"
                strokeWidth={0.4}
                strokeDasharray="1 2"
              />
            </svg>

            <div className="projection-chart-line-labels">
              {chartData.bars.map((bar, index) => (
                <div
                  key={`label-${bar.date}-${index}`}
                  className={`projection-chart-line-label ${index % labelStep === 0 ? 'visible' : ''}`}
                  style={{ left: `${bar.point.x}%`, top: `${Math.max(bar.point.y - 6, 2)}%` }}
                >
                  {currencyFormatter.format(bar.saldo)}
                </div>
              ))}
            </div>
          </div>

          <div className="projection-chart-axis">
            {data.map((point, index) => (
              <div
                key={`${point.date}-${index}`}
                className={`projection-chart-axis-item ${index % labelStep === 0 ? 'visible' : 'hidden'}`}
              >
                <span className="projection-chart-axis-date">{dateFormatter(point.date)}</span>
                <span className="projection-chart-axis-value">
                  {currencyFormatter.format(point.saldo)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Projection10DaysChart;
